import { useState, useEffect } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Activity, BarChart3, Shield, ChartBarIcon } from 'lucide-react';
import StatsCard from '@/components/dashboard/StatsCard';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import ApiStatus from '@/components/help/ApiStatus';
import { format } from 'date-fns';
import { healthService } from '@/services/healthService';

// Mock incident data
const recentIncidents = [
  /*{
    id: 'inc_1',
    title: 'API Performance Degradation',
    status: 'resolved',
    startTime: '2025-04-28T08:45:00Z',
    resolveTime: '2025-04-28T10:12:00Z',
    description: 'Our API experienced elevated latency for approximately 90 minutes due to an unexpected increase in traffic. Our engineering team added additional capacity to resolve the issue.',
    impactedServices: ['API', 'Webhooks'],
    severity: 'medium'
  },
  {
    id: 'inc_2',
    title: 'Dashboard Outage',
    status: 'resolved',
    startTime: '2025-04-15T14:23:00Z',
    resolveTime: '2025-04-15T15:47:00Z',
    description: 'The dashboard was unavailable for some users due to an issue with our CDN provider. Service was fully restored after we switched to a backup provider.',
    impactedServices: ['Dashboard'],
    severity: 'major'
  },
  {
    id: 'inc_3',
    title: 'Memory Storage Slow Queries',
    status: 'resolved',
    startTime: '2025-04-02T18:10:00Z',
    resolveTime: '2025-04-02T19:30:00Z',
    description: 'Some memory retrieval operations experienced increased latency due to a database index issue. The problem was resolved by rebuilding the affected indexes.',
    impactedServices: ['Memory Storage'],
    severity: 'minor'
  }*/
];

const StatusPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState<Array<{ name: string; value: string; change: number; isPositive: boolean }>>([]);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState<{ status: string; message: string; issues: string[] }>({
    status: 'operational',
    message: 'All systems are operating normally.',
    issues: []
  });

  // Fetch health metrics on component mount
  useEffect(() => {
    const fetchHealthMetrics = async () => {
      try {
        setLoading(true);
        const healthMetrics = await healthService.getSystemMetrics();
        setMetrics(healthMetrics);
        
        // Fetch and analyze system status
        const healthData = await healthService.getHealthData();
        const unhealthyChecks = healthData.checks.filter(check => check.status === 'Unhealthy');
        const degradedChecks = healthData.checks.filter(check => check.status === 'Degraded');
        
        let status = 'operational';
        let message = 'All systems are operating normally.';
        let issues: string[] = [];
        
        if (unhealthyChecks.length > 0 || degradedChecks.length > 0) {
          if (unhealthyChecks.length > 0) {
            status = 'degraded';
            message = `System experiencing issues. ${unhealthyChecks.length} service${unhealthyChecks.length > 1 ? 's' : ''} unhealthy.`;
            issues = unhealthyChecks.map(check => check.description);
          } else if (degradedChecks.length > 0) {
            status = 'degraded';
            message = `System performance degraded. ${degradedChecks.length} service${degradedChecks.length > 1 ? 's' : ''} experiencing issues.`;
            issues = degradedChecks.map(check => check.description);
          }
        }
        
        setSystemStatus({ status, message, issues });
      } catch (error) {
        console.error('Error fetching health metrics:', error);
        // Fallback to mock data if health service fails
        setMetrics([
          { name: 'API Response Time', value: 'N/A', change: 0, isPositive: false },
          { name: 'System Health', value: 'N/A', change: 0, isPositive: false },
          { name: 'Memory Usage', value: 'N/A', change: 0, isPositive: false },
          { name: 'CPU Usage', value: 'N/A', change: 0, isPositive: false }
        ]);
        setSystemStatus({
          status: 'unknown',
          message: 'Unable to determine system status.',
          issues: ['Health monitoring temporarily unavailable']
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHealthMetrics();
    
    // Refresh metrics every 60 seconds
    const interval = setInterval(fetchHealthMetrics, 60000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <PageLayout
      title="System Status"
      description="Monitor the health and performance of the Recallio platform"
    >
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-recallio-dark-gray rounded-lg border border-recallio-light-gray/50 p-6">
                  <div className="h-4 bg-recallio-medium-gray rounded mb-2"></div>
                  <div className="h-8 bg-recallio-medium-gray rounded mb-2"></div>
                  <div className="h-3 bg-recallio-medium-gray rounded w-1/2"></div>
                </div>
              </div>
            ))
          ) : (
            metrics.map((metric) => (
              <StatsCard
                key={metric.name}
                title={metric.name}
                value={metric.value}
                icon={<Activity size={18} className="text-recallio-yellow" />}
              />
            ))
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="incidents">Recent Incidents</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          <ApiStatus />
          
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Current Notices</h3>
            <div className="text-sm">
              <Badge 
                className={`mb-2 ${
                  systemStatus.status === 'operational' 
                    ? 'bg-recallio-green text-recallio-background'
                    : systemStatus.status === 'degraded'
                      ? 'bg-yellow-500 text-recallio-background'
                      : 'bg-red-500 text-recallio-background'
                }`}
              >
                {systemStatus.status === 'operational' ? 'Operational' : 
                 systemStatus.status === 'degraded' ? 'Degraded' : 
                 systemStatus.status === 'unknown' ? 'Unknown' : 'Issues'}
              </Badge>
              <p className="text-muted-foreground mb-3">{systemStatus.message}</p>
              {systemStatus.issues.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Current Issues:</h4>
                  <ul className="space-y-1">
                    {systemStatus.issues.map((issue, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start">
                        <span className="inline-block w-1 h-1 rounded-full bg-yellow-500 mt-2 mr-2 flex-shrink-0"></span>
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
        
        <TabsContent value="incidents" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Incident History</h3>
            <div className="space-y-6">
              {recentIncidents.map((incident) => (
                <div key={incident.id} className="border-b border-recallio-light-gray/50 last:border-0 pb-6 last:pb-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{incident.title}</h4>
                    <Badge 
                      className={
                        incident.status === 'resolved' 
                          ? 'bg-recallio-green text-recallio-background'
                          : incident.status === 'investigating'
                            ? 'bg-red-500'
                            : 'bg-yellow-500'
                      }
                    >
                      {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <Badge 
                      variant="outline" 
                      className={`
                        ${incident.severity === 'minor' ? 'border-yellow-500/50 bg-yellow-500/10 text-yellow-500' : ''}
                        ${incident.severity === 'medium' ? 'border-orange-500/50 bg-orange-500/10 text-orange-500' : ''}
                        ${incident.severity === 'major' ? 'border-red-500/50 bg-red-500/10 text-red-500' : ''}
                      `}
                    >
                      {incident.severity}
                    </Badge>
                    
                    <span className="text-muted-foreground">
                      {format(new Date(incident.startTime), 'PPp')} to {format(new Date(incident.resolveTime), 'PPp')}
                    </span>
                  </div>
                  
                  <p className="mt-2 text-sm">{incident.description}</p>
                  
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-xs text-muted-foreground">Affected services:</span>
                    {incident.impactedServices.map((service) => (
                      <Badge key={service} variant="outline" className="text-xs">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
};

export default StatusPage;
